import { useState, useMemo, useCallback } from "react";
import { useAuthStore } from "../store/useAuthStore";
import { useChangeEmail, useChangeName } from "../api/auth.queries";
import { ProfileChangeDetector } from "../utils/profile-changes.util";
import type {
  ProfileFormData,
  OriginalProfileData,
} from "../schemas/profile.schema";
import { toast } from "sonner";

/**
 * Hook for managing profile data with smart mutation handling
 * Follows separation of concerns - contains business logic for profile management
 */
export function useProfileManagement() {
  const { user, updateUser } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);

  // Create stable original data reference
  const originalData: OriginalProfileData = useMemo(
    () => ({
      firstName: user?.first_name || "",
      lastName: user?.last_name || "",
      email: user?.email || "",
    }),
    [user?.first_name, user?.last_name, user?.email],
  );

  const changeEmailMutation = useChangeEmail();
  const changeNameMutation = useChangeName();

  const isLoading =
    changeEmailMutation.isPending || changeNameMutation.isPending;

  /**
   * Handles saving profile changes with smart mutation calling
   * Only calls mutations for fields that have actually changed
   */
  const handleSave = useCallback(
    async (
      formData: ProfileFormData,
      changes: ReturnType<ProfileChangeDetector["detectChanges"]>,
    ) => {
      if (!user?.id) {
        throw new Error("User ID is required");
      }

      const promises: Promise<unknown>[] = [];
      let successCount = 0;
      const targetCount =
        (changes.hasEmailChanges ? 1 : 0) + (changes.hasNameChanges ? 1 : 0);

      const handleSuccess = () => {
        successCount++;
        if (successCount === targetCount) {
          // Update the local storage with new user data
          const userUpdates: Partial<typeof user> = {};

          if (changes.hasEmailChanges) {
            userUpdates.email = formData.email;
          }

          if (changes.hasNameChanges) {
            userUpdates.first_name = formData.firstName;
            userUpdates.last_name = formData.lastName;
          }

          updateUser(userUpdates); // Update local storage
          setIsEditing(false);
        }
      };

      const handleError = (error: unknown) => {
        console.error("Profile update error:", error);
        throw error; // Re-throw to be handled by the form component
      };

      // Only call changeEmail if email has changed
      if (changes.hasEmailChanges) {
        const emailPromise = new Promise<void>((resolve, reject) => {
          changeEmailMutation.mutate(
            {
              user_id: user.id,
              email: formData.email,
            },
            {
              onSuccess: () => {
                handleSuccess();
                resolve();
              },
              onError: (error) => {
                handleError(error);
                reject(error);
              },
            },
          );
        });
        promises.push(emailPromise);
      }

      // Only call changeName if name fields have changed
      if (changes.hasNameChanges) {
        const namePromise = new Promise<void>((resolve, reject) => {
          changeNameMutation.mutate(
            {
              email: formData.email, // API requires email for name change
              first_name: formData.firstName,
              last_name: formData.lastName,
            },
            {
              onSuccess: () => {
                handleSuccess();
                resolve();
              },
              onError: (error) => {
                handleError(error);
                reject(error);
              },
            },
          );
        });
        promises.push(namePromise);
      }

      // Wait for all mutations to complete
      if (promises.length > 0) {
        await Promise.all(promises);
      } else {
        // No changes detected, just exit edit mode
        setIsEditing(false);
        toast.info("No changes to save");
      }
    },
    [user?.id, changeEmailMutation, changeNameMutation, updateUser],
  );

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleEditToggle = useCallback(() => {
    setIsEditing(true);
  }, []);

  return {
    originalData,
    isEditing,
    isLoading,
    handleSave,
    handleCancel,
    handleEditToggle,
  };
}
